import _ from 'lodash';
import moment from 'moment';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import {
  ActionButton,
  ActionsBanner,
  BusinessUnitFilter,
  ButtonBadge,
  FiltersBar,
  Level,
  MemberFilter,
  Page,
  PeriodFilter,
  PracticeFilter,
  ProjectFilter,
  SplitButton,
  YesNoFilter,
} from '~/components';
import { ApproverFilter } from '~/components/filters';
import { useApi, useConfirmation, useSubscription, useToast, useWorkspace } from '~/contexts';
import {
  useActions,
  useAuth,
  useDocumentTitle,
  useFeatures,
  useForm,
  useRowSelection,
  useSearchParams,
  useSearchParamsConfig,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import settings from '~/settings';
import { dateFormats, intervalOptions } from '~/utils';
import intervalsByScope from '~/utils/intervalsByScope';
import ExpenseItemDrawer from '../item/ExpenseItemDrawer';
import ExpenseApprovalResults from './ExpenseApprovalResults';
import RejectExpenseDialog from './RejectExpenseDialog';
import SetToApprovedConfirmation from './SetToApprovedConfirmation';
import SetToRejectedConfirmation from './SetToRejectedConfirmation';

const handlers = {
  load: () => ({ action: 'load' }),
  refetch: () => ({ action: 'refetch' }),
  ready: ({ data }) => ({
    isReady: true,
    action: null,
    data,
  }),
  setParams: (params, state) => ({
    ...state,
    action: 'filter',
    query: { ...state.query, ...params },
    searchParamsStatus: 'ready',
  }),
  updateItems: (items, { data, query }) => ({
    data: data
      .map((result) => {
        let item = items.find((i) => i.id === result.id);
        if (!item) return result;

        return item ? { ...result, ...item } : result;
      })

      // Remove items from the queue if the status doesn't match the filter. This is mostly to
      // clear the queue when an approval action is taken.
      // This may eventually require a refetch, to exclude items based on other properties
      // (which may have changed when using the expense item drawer).
      .filter((result) => !query.status || result.statusId === query.status),
  }),
  removeItem: (id, { data }) => ({
    data: data.filter((i) => i.id !== id),
  }),
};

const stepIdsFor = (items) =>
  Object.fromEntries(items.filter((item) => item.approvalStepId).map((item) => [item.id, item.approvalStepId]));

function applyOutcomes({ rows, summary, statusId, announceSuccess, actions, refetchData, toast }) {
  actions.updateItems(rows.filter((row) => row.outcome === 'succeeded').map((row) => row.expenseItem));
  refetchData();

  if (announceSuccess && summary.succeeded > 0) {
    const verb = { approved: 'Approved', rejected: 'Rejected' }[statusId];
    toast.success(`${verb} ${summary.succeeded} expense ${pluralize('item', summary.succeeded)}.`);
  }

  const unresolved = summary.skipped + summary.failed;
  if (unresolved > 0) {
    const reasons = _.uniq(rows.filter((row) => row.outcome !== 'succeeded').map((row) => row.message));
    toast.warning(`${unresolved} expense ${pluralize('item', unresolved)} could not be updated. ${reasons.join(' ')}`);
  }
}

function ExpenseApprovalsPage({ parentUrl }) {
  const documentTitle = useDocumentTitle('Expense Approvals');

  const { workspace } = useWorkspace();
  const auth = useAuth();
  const features = useFeatures();

  const initialState = useMemo(
    () => ({
      isReady: false,
      searchParamsStatus: 'pending',
      data: null,
      query: {
        period: null,
        projects: [],
        members: [],
        memberBusinessUnits: [],
        memberPractices: [],
        approver: workspace.member,
        includeLockedItems: 'no',
        showNotes: 'no',
      },
      action: 'load',
    }),
    [workspace.member],
  );

  const [{ isReady, data, query, searchParamsStatus, action }, actions] = useActions(handlers, initialState);
  const enabledItems = useMemo(() => (data ?? []).filter((item) => !item.isLocked), [data]);
  const selection = useRowSelection({ rows: enabledItems });
  const [{ isSubmitting, saved }, form] = useForm();
  const { expenseItemId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const route = useRouteMatch();
  const api = useApi();
  const toast = useToast();
  const confirmation = useConfirmation();
  const { notify } = useSubscription();

  const searchParamsConfig = useSearchParamsConfig();

  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...searchParamsConfig.period,
          default: {
            ...intervalOptions.custom,
            start: moment().subtract(2, 'months').startOf('month').format(dateFormats.isoDate),
            end: moment().add(1, 'months').endOf('month').format(dateFormats.isoDate),
          },
        },
        sort: { default: initialState.query.sort, ...searchParamsConfig.sort },
        projects: searchParamsConfig.projects,
        members: searchParamsConfig.members,
        memberBusinessUnits: searchParamsConfig.memberBusinessUnits,
        memberPractices: searchParamsConfig.memberPractices,
        approver: {
          default: initialState.query.approver,
          ...searchParamsConfig.approver,
          deserialize: auth.workspace.manage
            ? searchParamsConfig.approver.deserialize
            : () => initialState.query.approver,
        },
        includeLockedItems: { default: initialState.query.includeLockedItems, valid: ['yes', 'no'] },
        showNotes: { default: 'no', valid: ['yes', 'no'] },
      }),
      [initialState.query, searchParamsConfig, auth.workspace.manage],
    ),
    sessionKey: 'expense_approvals',
    onChange: useCallback((params) => actions.setParams(params), [actions]),
  });

  useEffect(() => {
    if (searchParamsStatus !== 'pending') return;
    searchParams.get().then((params) => {
      if (params) actions.setParams(params);
    });
  }, [searchParams, searchParamsStatus, actions]);

  const fetchData = useCallback(async () => {
    try {
      const { start, end } = query.period || {};

      const params = {
        ..._.omit(query, [
          'projects',
          'members',
          'memberBusinessUnits',
          'memberPractices',
          'approver',
          'period',
          'includeLockedItems',
          'showNotes',
        ]),
        projectId: query.projects?.map((v) => v.id),
        memberId: query.members?.map((v) => v.id),
        memberBusinessUnitId: query.memberBusinessUnits?.map((v) => v.id),
        memberPracticeId: query.memberPractices?.map((v) => v.id),
        approverId: query.approver?.id,
        start: start ?? undefined,
        end: end ?? undefined,
        includeLockedItems: query.includeLockedItems ?? undefined,
      };

      const { data } = await api.www.workspaces(workspace.id).expenseAdmin().approvals(params);

      actions.ready({ data });
      return data;
    } catch (error) {
      actions.ready({ data: [], members: [] });
    }
  }, [actions, workspace.id, query, api]);

  const refetchData = async () => {
    actions.refetch();
    await fetchData();
  };

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const handleFilterChange = ({ target }) => {
    actions.setParams({ [target.name]: target.value });
    selection.clear();
    searchParams.set({ [target.name]: target.value });
  };

  const handleItemStatusChange = async (item, statusId) => {
    try {
      let notes;
      if (statusId === 'rejected') {
        notes = await confirmation.prompt((resolve) => (
          <RejectExpenseDialog count={1} onResolve={(notes) => resolve(notes)} />
        ));
        if (!notes) return;
      }

      form.submit(item.id);

      const { data: result } = await api.www
        .workspaces(workspace.id)
        .expenseAdmin()
        .batchUpdateStatus([{ ids: [item.id], stepIds: stepIdsFor([item]), statusId, notes }]);

      applyOutcomes({ ...result, statusId, announceSuccess: false, actions, refetchData, toast });

      notify(useSubscription.keys.refresh_expense_approval_count);
    } catch (error) {
      toast.error(error.message);
    }

    form.done();
  };

  const handleBatchStatusChange = async (statusId) => {
    try {
      let notes;
      if (statusId === 'rejected') {
        notes = await confirmation.prompt((resolve) => (
          <RejectExpenseDialog count={selection.count} onResolve={(notes) => resolve(notes)} />
        ));
        if (!notes) return;
      }

      form.submit('batch');

      const items = data.filter((item) => selection.isSelected(item.id));

      const { data: result } = await api.www
        .workspaces(workspace.id)
        .expenseAdmin()
        .batchUpdateStatus([{ ids: selection.ids, stepIds: stepIdsFor(items), statusId, notes }]);

      applyOutcomes({ ...result, statusId, announceSuccess: true, actions, refetchData, toast });
      selection.clear();
      form.save();
      notify(useSubscription.keys.refresh_expense_approval_count);
    } catch (error) {
      toast.error(error.message);
      form.done();
    }
  };

  const handleGroupAction = async (group, statusId) => {
    const ids = group.items.map((item) => item.id);

    try {
      const stepIds = stepIdsFor(group.items);

      let notes;
      if (statusId === 'rejected') {
        notes = await confirmation.prompt((resolve) => (
          <RejectExpenseDialog count={ids.length} onResolve={(notes) => resolve(notes)} />
        ));
        if (!notes) return;
      }

      form.submit({ action: 'group', group });

      const result = { rows: [], summary: { succeeded: 0, failed: 0, skipped: 0 } };
      for (const chunk of _.chunk(ids, settings.bulkActionItemLimit)) {
        const { data: chunkResult } = await api.www
          .workspaces(workspace.id)
          .expenseAdmin()
          .batchUpdateStatus([{ ids: chunk, stepIds: _.pick(stepIds, chunk), statusId, notes }]);
        result.rows.push(...chunkResult.rows);
        result.summary.succeeded += chunkResult.summary.succeeded;
        result.summary.failed += chunkResult.summary.failed;
        result.summary.skipped += chunkResult.summary.skipped;
      }

      applyOutcomes({ ...result, statusId, announceSuccess: true, actions, refetchData, toast });
      notify(useSubscription.keys.refresh_expense_approval_count);
    } catch (error) {
      refetchData();
      toast.error(error.message);
    }

    form.done();
  };

  const handleSetToApproved = async () => {
    await confirmation.prompt((resolve) => (
      <SetToApprovedConfirmation
        count={selection.count}
        onResolve={async (confirmed) => {
          if (!confirmed) {
            resolve(false);
            return;
          }

          try {
            const { data: result } = await api.www
              .workspaces(workspace.id)
              .expenseAdmin()
              .bulkSetApprovalStatus(selection.ids, { statusId: 'approved' });

            await refetchData();

            toast.success(
              `Set ${result.summary.succeeded} expense ${pluralize('item', result.summary.succeeded)} to Approved.`,
            );
            selection.clear();
            notify(useSubscription.keys.refresh_expense_approval_count);
            resolve(true);
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        }}
      />
    ));
  };

  const handleSetToRejected = async () => {
    await confirmation.prompt((resolve) => (
      <SetToRejectedConfirmation
        count={selection.count}
        onResolve={async (notes) => {
          if (!notes) {
            resolve(false);
            return;
          }

          try {
            const { data: result } = await api.www
              .workspaces(workspace.id)
              .expenseAdmin()
              .bulkSetApprovalStatus(selection.ids, { statusId: 'rejected', reason: notes });

            await refetchData();

            toast.success(
              `Set ${result.summary.succeeded} expense ${pluralize('item', result.summary.succeeded)} to Rejected.`,
            );
            selection.clear();
            notify(useSubscription.keys.refresh_expense_approval_count);
            resolve(true);
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        }}
      />
    ));
  };

  const handleResultClick = (item) => {
    history.push({ pathname: `${route.url}/item/${item.id}`, search: location.search, state: { scrollToTop: false } });
  };

  const handleCloseDrawer = () => {
    history.push({ pathname: parentUrl, search: location.search, state: { scrollToTop: false } });
    documentTitle.set('Expense Approvals');
  };

  const handleItemSaved = (item) => {
    actions.updateItems([item]);
    refetchData();
  };

  const handleItemDeleted = (item) => {
    actions.removeItem(item.id);
    refetchData();
    selection.deselect(item.id);
    handleCloseDrawer();
  };

  // Actions are only enabled if it's an approver's queue.
  const isApproverQueue = !!query.approver;
  const isAdminQueue = !query.approver && auth.workspace.manage;

  if (!isReady) return <PageLoader />;

  return (
    <>
      <Page>
        <Page.Header>
          <Page.Info>
            <Page.Eyebrow>Expenses</Page.Eyebrow>
            <Page.Title>Expense Approvals</Page.Title>
          </Page.Info>
        </Page.Header>

        <Page.Filters>
          <FiltersBar>
            <PeriodFilter
              name="period"
              placeholder="Date Range"
              intervals={intervalsByScope.day}
              value={query.period}
              onChange={handleFilterChange}
            />

            <ApproverFilter name="approver" value={query.approver} onChange={handleFilterChange} />

            <ProjectFilter name="projects" value={query.projects} onChange={handleFilterChange} />

            <MemberFilter name="members" value={query.members} onChange={handleFilterChange} />

            {features.businessUnits && (
              <BusinessUnitFilter
                name="memberBusinessUnits"
                placeholder="Member Business Unit"
                value={query.memberBusinessUnits}
                onChange={handleFilterChange}
              />
            )}

            {features.practices && (
              <PracticeFilter
                name="memberPractices"
                placeholder="Member Practice"
                value={query.memberPractices}
                onChange={handleFilterChange}
              />
            )}

            <YesNoFilter
              icon="filter"
              name="includeLockedItems"
              placeholder="Include Locked Items"
              value={query.includeLockedItems}
              onChange={handleFilterChange}
            />

            <YesNoFilter
              icon="gear"
              name="showNotes"
              placeholder="Show Notes"
              value={query.showNotes}
              onChange={handleFilterChange}
            />
          </FiltersBar>
        </Page.Filters>

        <ExpenseApprovalResults
          results={data}
          selection={selection}
          showNotes={query.showNotes}
          isSubmitting={isSubmitting}
          isApproverQueue={isApproverQueue}
          isAdminQueue={isAdminQueue}
          onResultClick={handleResultClick}
          onStatusChange={handleItemStatusChange}
          onGroupAction={handleGroupAction}
          onChange={refetchData}
          action={action}
        />

        {selection.any && (
          <ActionsBanner>
            <Level right>
              <Level.Item>
                {isApproverQueue && (
                  <SplitButton>
                    <ActionButton
                      isLoading={isSubmitting === 'batch'}
                      ok={saved}
                      onClick={() => handleBatchStatusChange('approved')}>
                      Approve <ButtonBadge visible={!saved && isSubmitting !== 'batch'}>{selection.count}</ButtonBadge>
                    </ActionButton>

                    <SplitButton.Menu position="top" disabled={selection.count === 0}>
                      {({ setIsOpen }) => (
                        <SplitButton.Item onClick={() => setIsOpen(false) || handleBatchStatusChange('rejected')}>
                          Reject
                        </SplitButton.Item>
                      )}
                    </SplitButton.Menu>
                  </SplitButton>
                )}

                {isAdminQueue && (
                  <SplitButton data-testid="admin-actions">
                    <ActionButton isLoading={isSubmitting === 'batch'} ok={saved} onClick={() => handleSetToApproved()}>
                      Set to Approved{' '}
                      <ButtonBadge visible={!saved && isSubmitting !== 'batch'}>{selection.count}</ButtonBadge>
                    </ActionButton>

                    <SplitButton.Menu position="top" disabled={selection.count === 0}>
                      {({ setIsOpen }) => (
                        <SplitButton.Item onClick={() => setIsOpen(false) || handleSetToRejected()}>
                          Set to Rejected
                        </SplitButton.Item>
                      )}
                    </SplitButton.Menu>
                  </SplitButton>
                )}
              </Level.Item>
            </Level>
          </ActionsBanner>
        )}
      </Page>

      {expenseItemId && (
        <ExpenseItemDrawer onSaved={handleItemSaved} onDeleted={handleItemDeleted} onClose={handleCloseDrawer} />
      )}
    </>
  );
}

export default ExpenseApprovalsPage;
